<template>
  <div id="knowledge-base" v-if="ready">
    <div class="kb-header" v-if="isDashboard">
      <h1>Knowledge Base Overview</h1>
      <div class="kb-controls">
        <knowledge-base-dashboard-filters v-if="isDashboard" @filters-updated="apiQueryString = $event" />
        <button @click="downloadInfo" class="btn btn-outlined download">Download <i class="fas fa-download" aria-hidden="true"></i></button>
      </div>
    </div>
    <div v-if="totalThreads > 0">
      <div class="toolbar-row">
        <div v-if="isApp" class="action-buttons">
          <button @click="toggleModal = true" class="btn btn-primary">Ask a question <i class="fas fa-plus" aria-hidden="true"></i></button>
        </div>
      </div>
      <div class="threads-container">
        <div class="thread-container-header">
          <div class="thread-count-header">
            <span>Displaying</span>
            <k-dropdown class="items-per-page-dropdown" v-if="totalThreads > 10" placeholder="" :show-search="false" :options="itemsPerPageOptions" v-model="itemsPerPage"></k-dropdown>
            <b v-else>{{ threads.length }}</b>
            <span>out of <b>{{ totalThreads }} threads</b></span>
          </div>
        </div>
        <div>
          <thread-card v-for="thread in threads"
            :key="thread.id"
            :thread="thread">
          </thread-card>
        </div>
      </div>
      <pagination v-if="numOfPages" v-model="currentPage" :count="numOfPages" :total-entries="totalThreads" :displayed-entries="threads.length"></pagination>
    </div>
    <div v-else class="kb-empty-state">
      <empty-placeholder
        title="No questions yet"
        :info="emptyStateInfo"
        fa-icon="fas fa-comments"
      />
      <div v-if="isApp" class="action-buttons">
        <button type="button" @click="toggleModal = true" class="btn btn-primary">
          Ask a question <i class="fas fa-plus" aria-hidden="true"></i>
        </button>
      </div>
    </div>
    <add-question
      v-if="isApp"
      :show="toggleModal"
      @added="handleNewQuestion"
      @close="toggleModal = false"
    />
  </div>
</template>

<style>
#knowledge-base .panel-pagination {
  padding: 0 15px 15px;
}

</style>

<style scoped>
.kb-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.kb-controls {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: space-between;
}

.toolbar-row,
.filters {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: space-between;
}

.filters .disabled {
  pointer-events: none;
}

.filters .disabled label {
  text-decoration: line-through;
}

.toolbar-row .filters button {
  background-color: transparent;
  border: var(--border-primary);
  color: var(--kate-type-light);
  padding: 12px;
}

.toolbar-row .filters button:hover {
  background-color: var(--kate-button-outlined-hover);
  color: var(--kate-type-dark);
}

.toolbar-row .filters button.active {
  background-color: var(--kate-button-outlined-hover);
  color: var(--kate-type-dark);
}

.threads-container {
  margin-top: 15px;
}

.thread-container-header {
  display: flex;
  justify-content: space-between;
  gap: 15px;
}

.thread-count-header {
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  column-gap: 10px;
}

.thread-count-header b {
  color: var(--kate-type-light);
}

.kb-empty-state {
  margin-top: 80px;
}

.kb-empty-state .action-buttons {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}
</style>

<script>
import useGlobalStore from '@stores/global';
import CsvMixin from '@mixins/csv-mixins';
import PageReadyMixin from '@mixins/page-ready-mixin';
import KDropdown from '@base-components/k-dropdown.vue';
import Pagination from '@base-components/k-pagination.vue';
import EmptyPlaceholder from '@base-components/empty-placeholder.vue';
import KnowledgeBaseDashboardFilters from './knowledge-base-dashboard-filters.vue';
import ThreadCard from './thread-card.vue';
import AddQuestion from './add-question.vue';

const PER_PAGE_LIMIT = 100;

export default {
  components: {
    ThreadCard,
    AddQuestion,
    KDropdown,
    Pagination,
    EmptyPlaceholder,
    KnowledgeBaseDashboardFilters,
  },

  mixins: [CsvMixin, PageReadyMixin],

  data() {
    return {
      store: useGlobalStore(),
      ready: false,
      toggleModal: false,
      threads: [],
      currentPage: 0,
      totalThreads: 0,
      itemsPerPage: 10,
      apiQueryString: '',
    };
  },

  beforeMount() {
    this.$Loading.start();
    this.getThreads();
  },

  watch: {
    numOfPages() {
      this.currentPage = 0;
    },

    currentPage() {
      this.getThreads();
    },

    apiQueryString() {
      this.getThreads();
    },

    itemsPerPage(val) {
      if (val > PER_PAGE_LIMIT) {
        this.itemsPerPage = PER_PAGE_LIMIT;
        return;
      }
      if (this.currentPage !== 0) {
        this.currentPage = 0;
        return;
      }
      this.getThreads();
    },
  },

  computed: {
    isDashboard() {
      return this.store.appName === 'dashboard';
    },

    isApp() {
      return this.store.appName === 'app';
    },

    threadsEndpoint() {
      const page = this.currentPage + 1;
      const limit = this.itemsPerPage;
      if (this.isDashboard) {
        return `/api/curriculum/knowledge/threads?${this.apiQueryString}&page=${page}&limit=${limit}`;
      }
      return `/api/curriculum/knowledge/${this.$route.params.moduleId}/threads?page=${page}&limit=${limit}`;
    },

    numOfPages() {
      if (this.totalThreads <= this.itemsPerPage) {
        return undefined;
      }
      return Math.ceil(this.totalThreads / this.itemsPerPage);
    },

    itemsPerPageOptions() {
      return [10, 20, 50, PER_PAGE_LIMIT]
        .filter(x => x < this.totalThreads)
        .concat(this.totalThreads > 0 && this.totalThreads < PER_PAGE_LIMIT ? [this.totalThreads] : []);
    },

    displayItemsDropdown() {
      return this.totalThreads > 10;
    },

    emptyStateInfo() {
      if (this.isApp) {
        return 'No questions have been posted yet. Be the first.';
      }
      return 'No questions match your filters, or none have been posted yet. Try changing your filters.';
    },
  },

  methods: {
    downloadInfo() {
      this.$http.get('/api/curriculum/knowledge/threads/info').then(result => {
        this.downloadCsvFromTable(
          {
            answered_by: {},
            answered_on: {},
            asked_by: {},
            assigned_to: {},
            awaiting_answer_from_learner: {},
            is_private: {},
            is_closed: {},
            is_discussion: {},
            is_bug_report: {},
            module_name: {},
            posted_on: {},
            programme_name: {},
            question_id: {},
          },
          result.data.threads,
          'info-activity.csv',
        );
      }).catch(err => {
        this.$logger.autowarn('Error retrieving threads', undefined, err);
        this.$ktoast.showError(err);
      }).then(() => {
        this.ready = true;
      });
    },

    getThreads() {
      this.ready = false;
      this.$http.get(this.threadsEndpoint).then(result => {
        this.threads = result.data.threads;
        this.totalThreads = result.data.total ?? 0;
      }).catch(err => {
        this.threads = [];
        this.totalThreads = 0;
        this.$logger.autowarn('Error retrieving threads', undefined, err);
        this.$ktoast.showError(err);
      }).then(() => {
        this.ready = true;
      });
    },

    handleNewQuestion() {
      this.getThreads();
      if (this.isApp) {
        this.$emit('refresh');
      }
    },
  },
};
</script>
